import { createSlice } from '@reduxjs/toolkit';
import {
  fetchDeliveryItems,
  createDeliveryItem,
  deleteDeliveryItem,
  setDeliveryItemsCount,
  updateDeliveryItemLogistics,
  updateDeliveryItemStatus,
} from './deliveryItemsThunks';

// Retrieve initial filters from local storage if available
const getInitialState = () => {
  const savedStateJSON = localStorage.getItem("deliveryItemsFilter");
  const defaults = {
    filters: { search: "" },
    pagination: { page: 1, rowsPerPage: 20 },
  };

  if (savedStateJSON) {
    try {
      const savedState = JSON.parse(savedStateJSON);
      return {
        filters: { search: savedState.search || defaults.filters.search },
        pagination: defaults.pagination,
      };
    } catch (e) {
      return defaults;
    }
  }
  return defaults;
};

const initialState = {
  data: [],
  count: 0,
  loading: false,
  error: null,
  ...getInitialState(),
};

const deliveryItemsSlice = createSlice({
  name: 'deliveryItems',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
      localStorage.setItem("deliveryItemsFilter", JSON.stringify(state.filters));
    },
    setPaginationPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setPaginationRowsPerPage: (state, action) => {
      state.pagination.page = 1;
      state.pagination.rowsPerPage = action.payload;
    },
    clearDeliveryItems: (state) => {
      state.data = [];
      state.count = 0;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch Delivery Items
      .addCase(fetchDeliveryItems.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryItems.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.results || [];
        state.count = action.payload.count || 0;
      })
      .addCase(fetchDeliveryItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Delivery Item
      .addCase(createDeliveryItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDeliveryItem.fulfilled, (state) => {
        // loading state will be cleared by the subsequent fetch call in the thunk
      })
      .addCase(createDeliveryItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Delivery Item
      .addCase(deleteDeliveryItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDeliveryItem.fulfilled, (state) => {
      })
      .addCase(deleteDeliveryItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Set Count — the response is the rebuilt list, so it replaces the page.
      .addCase(setDeliveryItemsCount.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.items;
        state.count = action.payload.count;
      })
      .addCase(setDeliveryItemsCount.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logistics — one row changes; refetching the page would be wasteful.
      .addCase(updateDeliveryItemLogistics.fulfilled, (state, action) => {
        state.data = state.data.map((item) =>
          item.id === action.payload.id ? action.payload : item
        );
      })
      // Status — the thunk refetches the page, so `fulfilled` has nothing to
      // apply; only the failure has to reach the table.
      .addCase(updateDeliveryItemStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(updateDeliveryItemStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { setFilters, setPaginationPage, setPaginationRowsPerPage, clearDeliveryItems } = deliveryItemsSlice.actions;
export default deliveryItemsSlice.reducer;
